/**
 * Display text size, including the Presentation size for a shared screen
 * (row 4bd890ab, founder direction #3615 6021504633 item 5).
 *
 * REUSED, NOT NEW. Profile -> Appearance -> Display already had a "Font Size"
 * setting (small / normal / large, saved in user_profiles.appearance_preferences
 * .font_size) that nothing ever applied: picking "Large" changed no pixel. This
 * module is what applies it, and adds the fourth value, "presentation".
 *
 * MECHANISM. The size sets the root font size (the <html> element) as a
 * percentage. Tailwind's type scale and spacing are rem-based, so text, padding
 * and rem-based widths grow together while px-based chrome (the 304px sidebar)
 * does not: the page gets bigger without being squeezed into the middle.
 *
 * Persisted per user in localStorage (key includes the user id, so a shared
 * demo laptop does not carry one person's projector setting to the next
 * sign-in). Every storage access is in a try/catch: private windows and
 * blocked site data throw, and the size then simply stays at Normal.
 */

export const FONT_SIZES = ["small", "normal", "large", "presentation"] as const;
export type FontSize = (typeof FONT_SIZES)[number];

/** Root font size per setting, as a percentage of the browser default (16px). */
export const FONT_SIZE_PERCENT: Record<FontSize, number> = {
  small: 87.5,
  normal: 100,
  large: 112.5,
  presentation: 125,
};

export const FONT_SIZE_LABEL: Record<FontSize, string> = {
  small: "Small",
  normal: "Normal",
  large: "Large",
  presentation: "Presentation",
};

const STORAGE_PREFIX = "ftl.display.font_size";
/** Fired on window when the size changes in this tab, so every reader re-syncs. */
export const FONT_SIZE_EVENT = "ftl:font-size-changed";

export function isFontSize(v: unknown): v is FontSize {
  return typeof v === "string" && (FONT_SIZES as readonly string[]).includes(v);
}

function storageKey(userId: string | null | undefined): string {
  return `${STORAGE_PREFIX}.${userId || "anonymous"}`;
}

export function readFontSize(userId: string | null | undefined): FontSize | null {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    return isFontSize(raw) ? raw : null;
  } catch {
    return null;
  }
}

function writeFontSize(userId: string | null | undefined, size: FontSize): void {
  try {
    window.localStorage.setItem(storageKey(userId), size);
  } catch {
    /* storage unavailable: the size still applies for this page view */
  }
}

/** Apply a size to the document root. Normal clears the override entirely. */
export function applyFontSize(size: FontSize, root: HTMLElement = document.documentElement): void {
  if (size === "normal") {
    root.style.removeProperty("font-size");
    delete root.dataset.fontSize;
  } else {
    root.style.fontSize = `${FONT_SIZE_PERCENT[size]}%`;
    root.dataset.fontSize = size;
  }
}

/** Choose a size for this user: store it, apply it now, tell other readers. */
export function setFontSize(userId: string | null | undefined, size: FontSize): void {
  writeFontSize(userId, size);
  applyFontSize(size);
  try {
    window.dispatchEvent(new CustomEvent(FONT_SIZE_EVENT, { detail: size }));
  } catch {
    /* no window events (non-browser): nothing else to tell */
  }
}
